// Tab global de Llamadas (Fase 5 de la integración CRM en Operations). Lista el
// log compartido de llamadas del equipo (GET /phone/calls, 1 fila por llamada
// real) con filtros de rango de fechas, agente, dirección, estado, teléfono y
// mercado, play de grabación cuando está disponible y link a la ficha del lead
// vinculado. Todo el contenido va envuelto en CrmConnectGate (auth por cookie
// del CRM). Read-only.
//
// PAGINACIÓN — CURSOR, no page/total. El endpoint del CRM es keyset (hasMore +
// nextCursor; totalApprox es null en cuanto hay filtros), así que no encaja en
// useCrmPagedList (page/total). Usamos useCrmCursorList: primera página +
// "Cargar más" acumulativo por cursor, con reset síncrono al cambiar filtros.

import { memo, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { useCrmCursorList, useCrmResource } from "../../hooks/useCrmResource";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  DIRECTION_FILTER_OPTIONS,
  DIRECTION_LABELS,
  STATUS_FILTER_OPTIONS,
  STATUS_LABELS,
  dirScheme,
  fmtDuration,
  listAdvisors,
  listPhoneCalls,
  statusScheme,
} from "../../api/crmCalls";
import {
  Market,
  PhoneCallDirectionFilter,
  PhoneCallListItem,
  PhoneCallStatus,
} from "../../api/crmTypes";
import { CrmConnectGate } from "../../context/CrmSessionContext";
import { Select } from "../../components/FormControls";
import {
  FilterCell,
  Td,
  Th,
  errorBox,
  inputStyle,
  pagerBtn,
  retryInline,
} from "../../components/crm/TableChrome";
import RecordingPlayer from "../../components/crm/RecordingPlayer";
import { downloadRecording } from "../../components/crm/downloadRecording";
import Pill from "../LeadDetail/components/Pill";
import { fmtDateTime } from "../../types/promos";

const PAGE_SIZE = 50;

// Mercados del filtro (mismo conjunto que el resto de vistas CRM: Market).
const MARKET_OPTIONS: { value: Market; label: string }[] = [
  { value: "ES", label: "España" },
  { value: "IT", label: "Italia" },
  { value: "FR", label: "Francia" },
];

export default function Calls() {
  return (
    <div style={{ padding: "1.5rem" }}>
      <CrmConnectGate>
        <CallsInner />
      </CrmConnectGate>
    </div>
  );
}

function CallsInner() {
  // Filtros. direction/status/market se tipan con la unión + '' ('' = "todos")
  // para que el value case el enum del CRM sin `as` en el builder del fetch.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [direction, setDirection] = useState<PhoneCallDirectionFilter | "">("");
  const [status, setStatus] = useState<PhoneCallStatus | "">("");
  const [market, setMarket] = useState<Market | "">("");
  const [phone, setPhone] = useState("");

  // Debounce del input de teléfono: el valor visible se actualiza al instante,
  // el refetch solo dispara al parar de teclear.
  const phoneQ = useDebouncedValue(phone.trim(), 300);

  // Los date-pickers también se debouncen (400ms) antes de alimentar el fetch:
  // teclear una fecha a mano dispara onChange por cada dígito de año, y sin
  // debounce cada uno lanzaría un refetch (y un reset del cursor).
  const dateFromQ = useDebouncedValue(dateFrom, 400);
  const dateToQ = useDebouncedValue(dateTo, 400);

  // El filtro de teléfono solo tiene sentido con ≥2 caracteres (el backend hace
  // match por sufijo/E.164); con 1 char devolvería demasiado ruido, así que no
  // lo enviamos hasta llegar al mínimo.
  const phoneParam = phoneQ.length >= 2 ? phoneQ : "";

  // Asesores para el desplegable de agente. GET /advisors es admin|sales en el
  // CRM: para un ops da 403 → la capa API lo devuelve como lista vacía
  // (fail-soft), así que el filtro cae a input de email libre sin romper la
  // vista. Paralelo e independiente del listado.
  const advisors = useCrmResource(
    () => listAdvisors(),
    [],
    "No se pudieron cargar los asesores",
  );

  const fetchPage = useCallback(
    (cursor?: string) =>
      listPhoneCalls({
        pageSize: PAGE_SIZE,
        sortDir: "desc",
        ...(cursor ? { cursor } : {}),
        ...(dateFromQ ? { dateFrom: dateFromQ } : {}),
        ...(dateToQ ? { dateTo: dateToQ } : {}),
        ...(agentEmail ? { agentEmail } : {}),
        ...(direction ? { direction } : {}),
        ...(status ? { status } : {}),
        ...(market ? { market } : {}),
        ...(phoneParam ? { phone: phoneParam } : {}),
      }).then((res) => ({
        items: res.items,
        nextCursor: res.hasMore ? res.nextCursor : null,
      })),
    [dateFromQ, dateToQ, agentEmail, direction, status, market, phoneParam],
  );

  const {
    items,
    loading,
    loadingMore,
    error,
    nextCursor,
    reload,
    loadMore,
  } = useCrmCursorList<PhoneCallListItem>(
    fetchPage,
    [dateFromQ, dateToQ, agentEmail, direction, status, market, phoneParam],
    "No se pudieron cargar las llamadas",
    "No se pudieron cargar más llamadas",
  );

  const agentOptions = useMemo(
    () =>
      (advisors.data ?? []).map((a) => ({
        value: a.email,
        label: a.name ? `${a.name} (${a.email})` : a.email,
      })),
    [advisors.data],
  );
  const hasAdvisors = agentOptions.length > 0;

  const list = items ?? [];
  const firstLoading = loading && items === null;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>
          Llamadas
        </h1>
        <span style={{ color: "#73879C", fontSize: ".82rem" }}>
          {firstLoading ? "Cargando…" : `${list.length} llamadas cargadas`}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.6rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <FilterCell label="Desde">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={inputStyle}
          />
        </FilterCell>
        <FilterCell label="Hasta">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={inputStyle}
          />
        </FilterCell>
        <FilterCell label="Agente">
          {hasAdvisors ? (
            <Select
              value={agentEmail}
              onChange={setAgentEmail}
              options={agentOptions}
              placeholder="Todos"
            />
          ) : (
            // Fail-soft: sin lista de asesores (ops en un CRM antiguo → 403, o
            // un error al cargarla) el agente se filtra por email libre.
            <input
              type="text"
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              placeholder="agente@life5.com"
              style={inputStyle}
            />
          )}
          {advisors.error && (
            // Error no-403 al cargar /advisors: avisamos discretamente pero el
            // input libre sigue operativo (no bloqueamos el filtro).
            <div
              style={{
                fontSize: ".68rem",
                color: "#b32424",
                marginTop: "0.2rem",
              }}
            >
              No se pudo cargar la lista de agentes
            </div>
          )}
        </FilterCell>
        <FilterCell label="Dirección">
          <Select
            value={direction}
            onChange={(v) => setDirection(v as PhoneCallDirectionFilter | "")}
            options={DIRECTION_FILTER_OPTIONS}
            placeholder="Todas"
          />
        </FilterCell>
        <FilterCell label="Estado">
          <Select
            value={status}
            onChange={(v) => setStatus(v as PhoneCallStatus | "")}
            options={STATUS_FILTER_OPTIONS}
            placeholder="Todos"
          />
        </FilterCell>
        <FilterCell label="Mercado">
          <Select
            value={market}
            onChange={(v) => setMarket(v as Market | "")}
            options={MARKET_OPTIONS}
            placeholder="Todos"
          />
        </FilterCell>
        <FilterCell label="Teléfono">
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+34… o últimos dígitos"
            style={inputStyle}
          />
        </FilterCell>
      </div>

      {error && (
        <div style={errorBox}>
          {error}{" "}
          <button type="button" onClick={reload} style={retryInline}>
            Reintentar
          </button>
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            minWidth: 920,
            borderCollapse: "collapse",
            fontSize: ".83rem",
          }}
        >
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th width={150}>Fecha</Th>
              <Th width="18%">Agente</Th>
              <Th width={110}>Dirección</Th>
              <Th width={140}>Teléfono</Th>
              <Th width="18%">Lead</Th>
              <Th width={90}>Duración</Th>
              <Th width={120}>Estado</Th>
              <Th width={110}>Grabación</Th>
            </tr>
          </thead>
          <tbody>
            {!firstLoading && !error && list.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin resultados.
                </td>
              </tr>
            )}
            {list.map((c) => (
              <CallRow key={c.callId} call={c} />
            ))}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <div style={{ marginTop: "0.8rem" }}>
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            style={pagerBtn(loadingMore)}
          >
            {loadingMore ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}
    </div>
  );
}

// memo: the list grows on each "Cargar más"; already-rendered rows have stable
// props (one row per callId), so memo skips re-rendering them.
const CallRow = memo(function CallRow({ call }: { call: PhoneCallListItem }) {
  // Dirección: si es un valor conocido usamos su etiqueta; si es histórico
  // migrado ('unknown' u otro) mostramos el valor crudo.
  const dirLabel =
    DIRECTION_LABELS[call.direction as PhoneCallDirectionFilter] ??
    call.direction;
  const statusLabel =
    STATUS_LABELS[call.status as PhoneCallStatus] ?? call.status;
  const phoneText = call.counterpartE164 ?? call.phoneLast9 ?? "—";
  const canPlay = call.recordingStatus === "available";

  return (
    <tr style={{ borderTop: "1px solid #f1f3f5" }}>
      <Td>{fmtDateTime(call.occurredAt)}</Td>
      <Td truncate maxWidth={200}>
        {call.agentName ? (
          <span title={call.agentName}>{call.agentName}</span>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>
        <Pill label={dirLabel} scheme={dirScheme(call.direction)} />
      </Td>
      <Td>{phoneText}</Td>
      <Td truncate maxWidth={200}>
        {call.leadId ? (
          // El leadId del CRM ES el ulid del monorepo → link directo a la ficha.
          <Link
            to={`/leads/${call.leadId}`}
            title={call.leadName ?? "Ver lead"}
            style={{ color: "#0069d9", fontWeight: 600 }}
          >
            {call.leadName ?? "Ver lead"}
          </Link>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>{fmtDuration(call.durationSec)}</Td>
      <Td>
        <Pill label={statusLabel} scheme={statusScheme(call.status)} />
      </Td>
      <Td>
        {canPlay ? (
          <>
            <RecordingPlayer callId={call.callId} />
            <RecordingDownload callId={call.callId} />
          </>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
    </tr>
  );
});

// Botón de descarga de la grabación (portado del CRM). Reutiliza la URL firmada
// de recording-url (lazy, al pulsar) y baja los bytes con nombre estable; si el
// fetch directo a GCS falla (CORS) degrada a abrir la URL en pestaña nueva.
function RecordingDownload({ callId }: { callId: string }) {
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onClick = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    setErr(null);
    try {
      await downloadRecording(callId);
    } catch (e) {
      setErr(
        e instanceof CrmHttpError
          ? describeCrmError(e)
          : "No se pudo descargar la grabación",
      );
    } finally {
      setDownloading(false);
    }
  }, [downloading, callId]);

  return (
    <div style={{ marginTop: "0.3rem" }}>
      <button
        type="button"
        onClick={onClick}
        disabled={downloading}
        style={downloadBtn}
        title="Descargar grabación"
      >
        {downloading ? "Descargando…" : "⬇ Descargar"}
      </button>
      {err && (
        <div style={{ fontSize: ".7rem", color: "#b32424" }}>{err}</div>
      )}
    </div>
  );
}

const downloadBtn: React.CSSProperties = {
  padding: "0.25rem 0.55rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".74rem",
  fontWeight: 600,
  cursor: "pointer",
};
